import { useCallback, useState } from 'react';
import { logger } from 'src/logger';

const STORAGE_KEY_PREFIX = 'recentCampaigns';
const MAX_RECENT_CAMPAIGNS = 5;

export interface RecentCampaign {
  campaignCmsUrl: string;
  title: string;
  createdAt: string;
}

/**
 * Returns the marketplace-scoped localStorage key.
 */
const getStorageKey = (marketplace: string): string => `${STORAGE_KEY_PREFIX}_${marketplace}`;

/**
 * Reads recent campaigns from localStorage for a given marketplace,
 * returning an empty array on failure.
 */
const loadFromStorage = (marketplace: string): RecentCampaign[] => {
  try {
    const raw = localStorage.getItem(getStorageKey(marketplace));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    logger.warn('Failed to parse recent campaigns from localStorage', { error });
    return [];
  }
};

/**
 * Hook to manage the last 5 created campaigns in localStorage, scoped per marketplace.
 * New entries are prepended; oldest is dropped when exceeding the limit.
 *
 * On addCampaign, reads the latest state from localStorage to avoid
 * overwriting entries created in other tabs, then writes synchronously
 * to prevent race conditions between read and write.
 */
export const useRecentCampaigns = (marketplace: string) => {
  const [recentCampaigns, setRecentCampaigns] = useState<RecentCampaign[]>(() =>
    loadFromStorage(marketplace),
  );

  const addCampaign = useCallback(
    (campaignCmsUrl: string, title: string) => {
      if (!campaignCmsUrl) {
        logger.warn('Attempted to add campaign with empty URL, skipping');
        return;
      }

      // Read fresh from localStorage to pick up entries from other tabs
      const current = loadFromStorage(marketplace);

      if (current.some((c) => c.campaignCmsUrl === campaignCmsUrl)) {
        logger.debug('Campaign URL already exists in recent campaigns, skipping', {
          campaignCmsUrl,
        });
        // Sync React state with cross-tab changes even on duplicate path
        setRecentCampaigns(current);
        return;
      }

      const entry: RecentCampaign = {
        campaignCmsUrl,
        title,
        createdAt: new Date().toISOString(),
      };
      const updated = [entry, ...current].slice(0, MAX_RECENT_CAMPAIGNS);

      try {
        localStorage.setItem(getStorageKey(marketplace), JSON.stringify(updated));
      } catch (error) {
        logger.warn('Failed to save recent campaigns to localStorage', { error });
      }

      setRecentCampaigns(updated);
    },
    [marketplace],
  );

  /**
   * Re-reads recent campaigns from localStorage into state.
   * Call when the consumer becomes visible to pick up changes from other components.
   */
  const refresh = useCallback(() => {
    setRecentCampaigns(loadFromStorage(marketplace));
  }, [marketplace]);

  return { recentCampaigns, addCampaign, refresh };
};
